import {
  Alert,
  Anchor,
  AspectRatio,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import {
  IconCheck,
  IconFileText,
  IconMail,
  IconShoppingCart,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { getCloudMetadata, getCurrentUser } from "../shared/getCurrentUser";
import { getErrorAsString } from "../shared/logging/getErrorAsString";
import { Log } from "../shared/logging/Log";
import { Login } from "../shared/Login";
import { IDistributorInfo, queryKeys, RestApi } from "../shared/RestApi";
import { NavBarContainer } from "./NavBarContainer";
import { NeedToRegister } from "./NeedToRegister";

// eslint-disable-next-line @typescript-eslint/no-require-imports
const dinlabeldistribuidores = require("url:../assets/DinLabelDistribuidores.pdf");

const panelStyle: React.CSSProperties = {
  background: "white",
  border: "1px solid var(--mantine-color-gray-2)",
  borderRadius: "var(--mantine-radius-md)",
  boxShadow: "0 8px 28px rgba(44, 45, 48, 0.05)",
};

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <Box
      p="lg"
      style={{
        background: "white",
        border: accent
          ? "1px solid var(--mantine-color-brand-3)"
          : "1px solid var(--mantine-color-gray-2)",
        borderRadius: "var(--mantine-radius-md)",
        boxShadow: "0 8px 28px rgba(44, 45, 48, 0.05)",
        flex: 1,
        minWidth: 140,
      }}
    >
      <Text size="sm" c="dimmed" fw={500} mb={4}>
        {label}
      </Text>
      <Text
        fz={32}
        fw={700}
        c={accent ? "brand.7" : "charcoal.8"}
        style={{ fontFamily: "var(--mantine-font-family-headings)" }}
        lh={1.1}
      >
        {value}
      </Text>
    </Box>
  );
}

function openAlertModal(options: {
  title: string;
  message: string;
  color?: string;
  onClose?: () => void;
}) {
  modals.open({
    title: options.title,
    centered: true,
    onClose: options.onClose,
    children: (
      <Stack gap="md">
        <Text size="sm">{options.message}</Text>
        <Button
          color={options.color ?? "brand"}
          onClick={() => {
            modals.closeAll();
          }}
        >
          OK
        </Button>
      </Stack>
    ),
  });
}

export function PortalDeDistribuidores() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const userInfo = getCurrentUser();
  const [searchParams] = useSearchParams();

  const {
    data: distributorInfo,
    isPending,
    isError,
  } = useQuery({
    queryKey: queryKeys.distributorInfo,
    queryFn: () => RestApi.getDistributorInfo(),
    enabled: !!userInfo,
  });

  React.useEffect(() => {
    if (!distributorInfo || searchParams.get("focus") !== "tutoriales") {
      return;
    }
    const el = document.getElementById("tutoriales");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [distributorInfo, searchParams]);

  const resendMutation = useMutation({
    mutationFn: ({
      rowKey,
      partitionKey,
      email,
    }: {
      rowKey: string;
      partitionKey: string;
      email: string;
    }) => RestApi.resendLicencia(rowKey, partitionKey, email),
  });

  const sendMutation = useMutation({
    mutationFn: (mail: string) => RestApi.sendLicencia(mail),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.distributorInfo });
    },
  });

  const resendLicense = (
    rowKey: string,
    partitionKey: string,
    email: string,
  ) => {
    if (!distributorInfo || !userInfo) {
      throw new Error("Distributor info should be defined");
    }

    Log.logger.info("Enviando licencia...");
    modals.openConfirmModal({
      title: t("licenses.sendTitle"),
      children: (
        <Text size="sm">{t("licenses.resendConfirm", { email })}</Text>
      ),
      labels: {
        confirm: t("dialogs.confirmYes"),
        cancel: t("dialogs.cancel"),
      },
      confirmProps: { color: "brand" },
      centered: true,
      onConfirm: () => {
        void (async () => {
          try {
            await resendMutation.mutateAsync({ rowKey, partitionKey, email });
          } catch (error) {
            openAlertModal({
              title: t("licenses.errorTitle"),
              message: t("licenses.sendError", {
                error: getErrorAsString(error),
              }),
              color: "red",
            });
            return;
          }

          openAlertModal({
            title: t("licenses.resentTitle"),
            message: t("licenses.resentMessage", {
              email,
              cc: distributorInfo.CloudUser.AccountEmail,
            }),
            color: "brand",
          });
        })();
      },
    });
  };

  const onEnviarLicenciasClick = () => {
    if (!distributorInfo || !userInfo) {
      throw new Error("Distributor info should be defined");
    }

    Log.logger.info("Enviando licencia...");
    let emailValue = "";

    modals.openConfirmModal({
      title: t("licenses.sendTitle"),
      children: (
        <>
          <Text size="sm" mb="sm">
            {t("licenses.sendPrompt")}
          </Text>
          <TextInput
            placeholder={t("licenses.emailPlaceholder")}
            data-autofocus
            onChange={(event) => {
              emailValue = event.currentTarget.value;
            }}
          />
        </>
      ),
      labels: {
        confirm: t("dialogs.send"),
        cancel: t("dialogs.cancel"),
      },
      confirmProps: { color: "brand" },
      centered: true,
      onConfirm: () => {
        const mail = emailValue.trim();
        if (!mail) {
          return;
        }

        void (async () => {
          try {
            await sendMutation.mutateAsync(mail);
          } catch (error) {
            openAlertModal({
              title: t("licenses.errorTitle"),
              message: t("licenses.sendError", {
                error: getErrorAsString(error),
              }),
              color: "red",
            });
            return;
          }

          openAlertModal({
            title: t("licenses.sentTitle"),
            message: t("licenses.sentMessage", {
              email: mail,
              cc: distributorInfo.CloudUser.AccountEmail,
            }),
            color: "brand",
          });
        })();
      },
    });
  };

  const renderLicensesSentTable = (
    info: IDistributorInfo,
  ): React.JSX.Element | null => {
    if (info.DistributorLicenseMailMappings.length <= 0) {
      return null;
    }

    return (
      <Box p="xl" style={panelStyle}>
        <Title order={4} c="charcoal.8" mb="md">
          {t("licenses.sentHistory")}
        </Title>
        <Table.ScrollContainer minWidth={560}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("licenses.recipient")}</Table.Th>
                <Table.Th>{t("licenses.date")}</Table.Th>
                <Table.Th>{t("licenses.sent")}</Table.Th>
                <Table.Th>{t("licenses.activated")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {info.DistributorLicenseMailMappings.map((row) => {
                const sentDate = new Date(row.InsertionTime);
                return (
                  <Table.Tr key={row.LicenseId}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {row.LicenseInvitationEmail}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {sentDate.toLocaleDateString(i18n.language)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <IconCheck
                        size={18}
                        color="var(--mantine-color-green-6)"
                      />
                    </Table.Td>
                    <Table.Td>
                      {!row.AcceptedByUserId && (
                        <Stack gap="xs">
                          <Badge color="orange" variant="light" w="fit-content">
                            {t("licenses.notActivated")}
                          </Badge>
                          <Button
                            color="brand"
                            size="xs"
                            variant="light"
                            w="fit-content"
                            loading={resendMutation.isPending}
                            onClick={() => {
                              resendLicense(
                                row.LicenseId,
                                info.CloudUser.UserId,
                                row.LicenseInvitationEmail,
                              );
                            }}
                          >
                            {t("licenses.resend")}
                          </Button>
                        </Stack>
                      )}
                      {row.AcceptedByUserId && (
                        <Badge
                          color="green"
                          variant="light"
                          leftSection={<IconCheck size={12} />}
                        >
                          {t("licenses.activatedYes")}
                        </Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Box>
    );
  };

  const renderAuthorizedContent = (
    info: IDistributorInfo,
  ): React.JSX.Element => {
    const sent = info.LicensesBought - info.AvailableLicenses;

    return (
      <Stack gap="lg">
        <Group gap="md" align="stretch" wrap="wrap" grow preventGrowOverflow={false}>
          <StatCard
            label={t("licenses.statAvailable")}
            value={info.AvailableLicenses}
            accent
          />
          <StatCard label={t("licenses.statBought")} value={info.LicensesBought} />
          <StatCard label={t("licenses.statSent")} value={sent} />
        </Group>

        {info.AvailableLicenses <= 0 && (
          <Alert color="orange" variant="light">
            {t("licenses.noneAvailable")}
          </Alert>
        )}

        <Box p="xl" style={panelStyle}>
          <Title order={4} c="charcoal.8" mb="md">
            {t("portal.actions")}
          </Title>
          <Group gap="md" wrap="wrap">
            {info.AvailableLicenses > 0 && (
              <Button
                leftSection={<IconMail size={16} />}
                onClick={onEnviarLicenciasClick}
                loading={sendMutation.isPending}
                color="brand"
              >
                {t("licenses.sendToEmail")}
              </Button>
            )}
            <Button
              component={Link}
              to="/ComprarLicencias"
              leftSection={<IconShoppingCart size={16} />}
              variant={info.AvailableLicenses > 0 ? "outline" : "filled"}
              color="brand"
            >
              {t("portal.buyLicenses")}
            </Button>
          </Group>
        </Box>

        {renderLicensesSentTable(info)}
      </Stack>
    );
  };

  if (!userInfo) {
    return (
      <NavBarContainer>
        <Box py="xl" maw={480} mx="auto">
          <Login />
        </Box>
      </NavBarContainer>
    );
  }

  if (isPending) {
    return (
      <NavBarContainer title={t("portal.pageTitle")}>
        <Group gap="sm" c="dimmed" py="xl">
          <Loader size="sm" color="brand" />
          <Text>{t("portal.loading")}</Text>
        </Group>
      </NavBarContainer>
    );
  }

  if (isError) {
    return (
      <NavBarContainer title={t("portal.pageTitle")}>
        <Alert color="red" variant="light" mt="md">
          {t("portal.loadError")}
        </Alert>
      </NavBarContainer>
    );
  }

  if (!distributorInfo) {
    return (
      <NavBarContainer title={t("portal.pageTitle")}>
        <Box
          mt="md"
          p="xl"
          maw={640}
          style={panelStyle}
        >
          <NeedToRegister />
        </Box>
      </NavBarContainer>
    );
  }

  return (
    <NavBarContainer title={t("portal.pageTitle")}>
      <Stack gap="xl" py="md">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Stack gap={4}>
            <Title order={2} c="charcoal.8">
              {t("portal.greeting", { name: userInfo.Name })}
            </Title>
            <Text c="dimmed">
              {distributorInfo.DistributorInfo.CompanyName}
              {" · "}
              {distributorInfo.CloudUser.AccountEmail}
            </Text>
          </Stack>
          {distributorInfo.DistributorInfo.DistribuidorAutorizado ? (
            <Badge color="brand" variant="light" size="lg">
              {t("portal.authorized")}
            </Badge>
          ) : (
            <Badge color="orange" variant="light" size="lg">
              {t("portal.pending")}
            </Badge>
          )}
        </Group>

        {distributorInfo.DistributorInfo.DistribuidorAutorizado &&
          renderAuthorizedContent(distributorInfo)}

        {!distributorInfo.DistributorInfo.DistribuidorAutorizado && (
          <Box p="xl" style={panelStyle}>
            <Stack gap="md">
              <Alert color="orange" variant="light" title={t("portal.pending")}>
                {t("portal.pendingApproval", {
                  email: getCloudMetadata().DistributorSupportEmail,
                })}
              </Alert>
              <Anchor
                href={dinlabeldistribuidores}
                c="brand.7"
                fw={600}
                w="fit-content"
              >
                <Group gap={6}>
                  <IconFileText size={16} />
                  {t("portal.distributorInfo")}
                </Group>
              </Anchor>
            </Stack>
          </Box>
        )}

        <Box id="tutoriales" p="xl" style={panelStyle}>
          <Stack gap="md">
            <Title order={4} c="charcoal.8">
              {t("tutorials.title")}
            </Title>
            <Text size="sm" c="dimmed">
              {t("tutorials.alsoSeeFaqBefore")}{" "}
              <Anchor component={Link} to="/faq" c="brand.7" fw={600}>
                {t("tutorials.alsoSeeFaqLink")}
              </Anchor>
            </Text>
            <Text fw={500}>{t("tutorials.sendLicenseTutorial")}</Text>
            <AspectRatio ratio={16 / 9} maw={640}>
              <iframe
                title={t("tutorials.iframeTitle")}
                src="https://www.youtube.com/embed/FF3ZZ6gjJLE"
                allowFullScreen
                style={{ border: 0 }}
              />
            </AspectRatio>
          </Stack>
        </Box>
      </Stack>
    </NavBarContainer>
  );
}
